import React, { ComponentPropsWithoutRef } from 'react';
import styled, { css } from 'styled-components';
import { formOpacities } from '../formOpacities';
import { withCssSelector } from '../../shared/withCssSelector';
import { Input } from '../input';
import { Checkbox, CheckboxSize, checkboxSizes } from '../checkbox';
import { Radio } from '../radio';
import { ErrorBlock } from '../error-block';
import { HelpText } from '../help-text';

const StyledInputLabel = styled.label<{
	$disabled?: boolean | undefined;
	$hasError?: boolean | undefined;
	$size: CheckboxSize;
}>`
	font-family: ${props => props.theme.font};
	font-style: normal;
	font-weight: 400;
	font-size: 14px;
	line-height: 20px;
	color: ${props => props.$hasError ? props.theme.form.error.color : props.theme.form.label.dimmed};
	opacity: ${props => props.$disabled ? formOpacities.disabled : formOpacities.full};
	margin: 0;
	pointer-events: ${props => props.$disabled ? 'none' : 'auto'};
	position: relative;

	// The whole row is the click target: control plus label, sized vertically by
	// the checkbox size and hugging its content so no dead space is clickable.
	// Clicking a <label> already toggles its control.
	display: inline-flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 0 8px;
	width: fit-content;
	min-height: ${props => props.theme.form.checkbox.target[props.$size]}px;

	${props => props.$disabled || props.$hasError ? '' : css`
		&:has(input:checked),
		&:has(input:indeterminate),
		&:hover,
		&:active {
			color: ${props.theme.form.label.color};
		}

		// Engaging a checked row dims it, the same way its box dims. Last, so it
		// wins over both rules above.
		&:hover:has(input:checked),
		&:hover:has(input:indeterminate),
		&:active:has(input:checked),
		&:active:has(input:indeterminate) {
			color: ${props.theme.form.label.dimmed};
		}
	`}

	${Input.cssSelector}[type="checkbox"],
	${Input.cssSelector}[type="radio"],
	${Radio.cssSelector},
	${Checkbox.cssSelector} {
		flex: none;
	}

	${props => props.$disabled ? '' : css`
		cursor: pointer;
	`}
	// Break out of the label's own row rather than sitting beside the control.
	${ErrorBlock.cssSelector},
	${HelpText.cssSelector} {
		display: block;
		flex-basis: 100%;
	}
`;

export type InputLabelProps = {
	disabled?: boolean | undefined
	hasError?: boolean | undefined
	size?: CheckboxSize | undefined
} & ComponentPropsWithoutRef<'label'>

export const InputLabel = withCssSelector(React.forwardRef<HTMLLabelElement, InputLabelProps>(
	function InputLabel({ disabled, hasError, size = checkboxSizes.compact, ...props }, ref) {
		return <StyledInputLabel
			ref={ref}
			$disabled={disabled}
			$hasError={hasError}
			$size={size}
			{...props}
		/>;
	}
), StyledInputLabel.toString());
